import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api"; // Your shared axios instance
import { hideLoader, showLoader } from "./loaderSlice";

// Thunk for fetching all groups
export const getGroupList = createAsyncThunk(
  "group/getGroupList",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(`/getall/clinic/groupNames`);
      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data || [];
      }
      thunkAPI.dispatch(hideLoader());
      return [];
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch groups");
    }
  }
);

// Thunk for creating a group
export const createGroup = createAsyncThunk(
  "group/createGroup",
  async ({ groupName }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(`/create/clinic/groupName?groupName=${groupName}`);
      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to create group");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

// Thunk for updating a group
export const updateGroup = createAsyncThunk(
  "group/updateGroup",
  async ({ groupId, groupName }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/updated/clinic/groupName/${groupId}?groupName=${groupName}`
      );
      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to update group");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

// Thunk for deleting a group
export const deleteGroup = createAsyncThunk(
  "group/deleteGroup",
  async ({ groupId }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).delete(`/delete/clinic/groupName/${groupId}`);
      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return groupId; // Return ID to remove from list
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to delete group");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

// Slice
const groupSlice = createSlice({
  name: "group",
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getGroupList.fulfilled, (state, action) => {
        state.list = action.payload ?? [];
        state.loading = false;
        state.error = null;
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        state.list.push(action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(updateGroup.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.list.findIndex((g) => g.roomId === updated.roomId);
        if (index !== -1) {
          state.list[index] = updated;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteGroup.fulfilled, (state, action) => {
        const id = action.payload;
        state.list = state.list.filter((g) => g.roomId !== id);
        state.loading = false;
        state.error = null;
      })
      .addCase(getGroupList.pending, (state) => {
        state.loading = true;
      })
      .addCase(createGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(getGroupList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching groups.";
      });
  },
});

export default groupSlice.reducer;